import { HStack, Table, Tbody, Td, Text, Thead, Tr } from "@chakra-ui/react"
import { Dictionary, PrinticularLineItem } from "@jackfruit/common"
import { groupBy, orderBy } from "lodash"
import React from "react"
import { useTranslation } from "react-i18next"
import { OrderEntity } from "~/interfaces/entities/Order"
import {
  OrderSummaryEntity,
  RemoteOrderSummaryLineItem,
} from "~/interfaces/entities/OrderSummary"
import { formatCurrency } from "~/services/Utils"

export interface SummaryLine {
  label: string
  total: number
  subTotal: number
  discount?: number
  quantity: number
}

interface PriceProps {
  lineItem: SummaryLine
  currency: string
}

const PriceWithDiscount: React.FC<PriceProps> = ({ lineItem, currency }) => {
  const { discount, subTotal, total } = lineItem

  if (discount) {
    return (
      <HStack spacing={2} justifyContent="flex-end">
        <Text color="red.500">{formatCurrency(currency, total)}</Text>
        <Text as="s">{formatCurrency(currency, subTotal)}</Text>
      </HStack>
    )
  } else {
    return <Text>{formatCurrency(currency, subTotal)}</Text>
  }
}

const sortAndGroupLineItems = (
  lineItems: RemoteOrderSummaryLineItem[] | PrinticularLineItem[]
) => {
  const groupedLineItems = groupBy(lineItems, "product.id") as Dictionary<
    RemoteOrderSummaryLineItem[]
  >

  // sort by product short description
  const sortedLineItems = orderBy(Object.values(groupedLineItems), [
    groupedLineItem => groupedLineItem[0].product.shortDescription,
  ])

  return sortedLineItems
}

export const SummaryLineItems = ({
  summaryLines,
  currency,
}: {
  summaryLines: SummaryLine[]
  currency: string
}) => {
  const { t } = useTranslation()

  return (
    <Table colorScheme="facebook" data-testid="order-summary-lineitems-table">
      <Thead>
        <Tr>
          <Td
            fontSize={{ base: "sm", sm: "md" }}
            border="none"
            px={0}
            pt={0}
            textAlign="left"
          >
            <Text fontWeight="bold" color={"inherit"}>
              {t("components.checkout.OrderSummaryLineItems.Products")}
            </Text>
          </Td>
          <Td
            fontSize={{ base: "sm", sm: "md" }}
            border="none"
            px={0}
            pt={0}
            textAlign="center"
          >
            <Text fontWeight="bold" color={"inherit"}>
              {t("components.checkout.OrderSummaryLineItems.Quantity")}
            </Text>
          </Td>
          <Td
            fontSize={{ base: "sm", sm: "md" }}
            border="none"
            px={0}
            pt={0}
            textAlign="right"
          >
            <Text fontWeight="bold" color={"inherit"}>
              {t("components.checkout.OrderSummaryLineItems.TotalPrice")}
            </Text>
          </Td>
        </Tr>
      </Thead>
      <Tbody>
        {summaryLines.map((lineItem, index) => {
          return (
            <Tr key={index}>
              <Td border="none" px={0}>
                <Text fontSize="sm">{lineItem.label}</Text>
              </Td>
              <Td border="none" px={0} textAlign="center">
                <Text fontSize="sm">{lineItem.quantity}</Text>
              </Td>
              <Td border="none" px={0} textAlign="right">
                <PriceWithDiscount lineItem={lineItem} currency={currency} />
              </Td>
            </Tr>
          )
        })}
      </Tbody>
    </Table>
  )
}

interface OrderSummaryProps {
  order: OrderSummaryEntity | OrderEntity
}

const OrderSummaryLineItems: React.FC<OrderSummaryProps> = ({ order }) => {
  const { lineItems, currency } = order.data

  const groupedAndSortedLineItems = sortAndGroupLineItems(lineItems)

  const summaryLines: SummaryLine[] = groupedAndSortedLineItems.map(
    lineItemsGroup => {
      return lineItemsGroup.reduce(
        (line, current) => {
          line.label = current.product.shortDescription
          line.quantity += current.quantity
          line.total += current.totalFloat
          line.subTotal += current.subtotalFloat

          return line
        },
        {
          label: "",
          quantity: 0,
          total: 0,
          subTotal: 0,
        }
      )
    }
  )

  return <SummaryLineItems summaryLines={summaryLines} currency={currency} />
}

export default OrderSummaryLineItems
